import React, { useEffect, useState, useCallback, lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom'
import { Toaster } from 'sonner'

import HttpClient from '../services/HttpClient'

import AppContext from '../contexts/AppContext'

/** Components */
import Navbar from '../components/Navbar/Navbar'
import CharNavbar from '../components/Navbar/CharNavbar'
import SiteFooter from '../components/SiteFooter/SiteFooter'
/** Dashboard */
import DashNewRank from '../pages/Dashboard/Characters/New/NewRank'
import DashNewStat from '../pages/Dashboard/Characters/New/NewStat'
import DashNewShogunato from '../pages/Dashboard/Characters/New/NewShogunato'
/** Index **/
import Home from '../pages/Home/Home'
/** User Management **/
import Register from '../pages/Register/Register'
import Login from '../pages/Login/Login'
import ComingSoon from '../pages/ComingSoon/ComingSoon'
import ComingSoonGate from '../components/ComingSoonGate/ComingSoonGate'
import Profile from '../pages/Profile/Profile'
/** Territory Management **/
import ViewTerritory from '../pages/Territory/View/ViewTerritory'
import ViewHanArchives from '../pages/Territory/Archives/ViewHanArchives'
/** Characters Management */
import CharactersMain from '../pages/Characters/index'
import CharactersDirectoryPage from '../pages/Characters/Directory/CharactersDirectoryPage'
import CreateCharacterWizard from '../pages/Characters/Create/CreateCharacterWizard'
import CharacterDetailLayout from '../pages/Characters/ViewSheet/CharacterDetailLayout'
import CharacterFicha from '../pages/Characters/ViewSheet/CharacterFicha'
import MissionIssuerNpcSheet from '../pages/Characters/ViewSheet/MissionIssuerNpcSheet'
import CharacterHistoria from '../pages/Characters/ViewSheet/CharacterHistoria'
import CharacterProgresion from '../pages/Characters/ViewSheet/CharacterProgresion'
import RoleSystemsIndex from '../pages/RoleSystems/RoleSystemsIndex'
import ViewRoleSystem from '../pages/RoleSystems/ViewRoleSystem'
import ViewCharacterSheet from '../pages/Characters/ViewSheet/ViewCharacterSheet'
import AcademyBasicsTechniques from '../pages/Characters/ViewSheet/AcademyBasicsTechniques'
import ClansDirectory from '../pages/Clans/Directory/ClansDirectory'
import ViewClan from '../pages/Clans/View/ViewClan'
import TerritoriesIndex from '../pages/Territory/Index/TerritoriesIndex'
import usePresenceHeartbeat from '../hooks/usePresenceHeartbeat'

/** Stylesheets */
import '../components/Page/Page.css'
import '../components/Form/Form.css'
import { paths, LEGACY_REDIRECTS } from './paths'

/** Heavy routes — code-split out of the initial bundle */
const ViewAdventure = lazy(() => import('../features/adventure/View/ViewAdventure'))
const CreateAdventure = lazy(() => import('../features/adventure/Create/CreateAdventure'))
const ReplyAdventure = lazy(() => import('../features/adventure/Reply/ReplyAdventure'))
const AdventuresIndex = lazy(() => import('../features/adventure/Index/AdventuresIndex'))
const MissionSheet = lazy(() => import('../features/missions/View/MissionSheet'))
const WorldMap = lazy(() => import('../pages/Map/WorldMap'))
const AdminRouteGuard = lazy(() => import('../admin/AdminRouteGuard'))

function RouteFallback() {
  return <div style={{ padding: '2rem', textAlign: 'center' }}>Cargando…</div>
}

function InventoryRedirect() {
  const { slug } = useParams()
  return <Navigate to={paths.personajeEquipo(slug)} replace />
}

function LegacyRedirect({ to }) {
  const params = useParams()
  const target = typeof to === 'function' ? to(params) : to
  return <Navigate to={target} replace />
}

/**
 * Stable route tree — must live outside App so React does not remount
 * the whole tree on every App state change.
 */
function AppRoutes({
  user,
  showNav,
  showCharNav,
  setShowNav,
  setShowCharNav,
  permsLoaded,
  permsError,
  hasAdminPerm,
}) {
  const location = useLocation()
  const isAdminRoute = location.pathname.startsWith('/admin')

  // Nav por ruta: lista/detalle sin Navbar global; resto la restaura (evita sticky false).
  useEffect(() => {
    const path = location.pathname
    const isList = path === paths.misPersonajes
    const isNpcSheet = /^\/personajes\/[^/]+\/npc\/?$/.test(path)
    const isDetail =
      !isNpcSheet &&
      /^\/personajes\/(?!crear(?:\/|$))[^/]+(?:\/(?:ficha|tecnicas|equipo|historia|progresion))?\/?$/.test(
        path
      )
    const isAuthOrComingSoon =
      path === paths.proximamente ||
      path === paths.entrar ||
      path === paths.registro ||
      path.startsWith('/auth/')

    if (isAuthOrComingSoon) {
      setShowNav(false)
      setShowCharNav(false)
    } else if (isList || isNpcSheet) {
      setShowNav(false)
      setShowCharNav(false)
    } else if (isDetail) {
      setShowNav(false)
      setShowCharNav(true)
    } else {
      setShowNav(true)
      setShowCharNav(false)
    }
  }, [location.pathname, setShowNav, setShowCharNav])

  return (
    <ComingSoonGate user={user}>
      <>
        {showNav && !isAdminRoute && <Navbar />}
        {showCharNav && !isAdminRoute && <CharNavbar />}
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path={paths.proximamente} element={<ComingSoon />} />
            <Route path={paths.home} element={<Home />} />
            <Route path={paths.registro} element={<Register />} />
            <Route path={paths.entrar} element={<Login />} />
            <Route path={paths.perfil} element={<Profile />} />

            {/** HAN // FOROS */}
            <Route path="/han/:slug/archivos" element={<ViewHanArchives />} />
            <Route path="/han/:slug" element={<ViewTerritory />} />

            {/** AVENTURA // POSTS */}
            <Route path="/aventura/crear/:slug" element={<CreateAdventure />} />
            <Route path="/aventura/:slug" element={<ViewAdventure />} />
            <Route path="/aventura/responder/:slug" element={<ReplyAdventure />} />
            <Route path="/misiones/:id" element={<MissionSheet />} />

            {/** CLANES */}
            <Route path={paths.clanes} element={<ClansDirectory />} />
            <Route path="/clanes/:idOrSlug" element={<ViewClan />} />

            {/* ÍNDICES */}
            <Route path={paths.aventuras} element={<AdventuresIndex />} />
            <Route path={paths.hans} element={<TerritoriesIndex />} />
            <Route path={paths.sistemasDeRol} element={<RoleSystemsIndex />} />
            <Route path="/sistemas-de-rol/:slug" element={<ViewRoleSystem />} />

            {/* PERSONAJES */}
            <Route
              path={paths.misPersonajes}
              element={<CharactersMain funcNav={setShowNav} funcCharNavbar={setShowCharNav} />}
            />
            <Route path={paths.personajeCrear} element={<CreateCharacterWizard />} />
            <Route
              path={paths.personajes}
              element={<CharactersDirectoryPage funcNav={setShowNav} funcCharNavbar={setShowCharNav} />}
            />
            <Route
              path="/personajes/:slug/npc"
              element={
                <MissionIssuerNpcSheet
                  funcNav={setShowNav}
                  funcCharNavbar={setShowCharNav}
                />
              }
            />
            <Route
              path="/personajes/:slug"
              element={<CharacterDetailLayout funcNav={setShowNav} funcCharNavbar={setShowCharNav} />}
            >
              <Route index element={<Navigate to="ficha" replace />} />
              <Route path="ficha" element={<CharacterFicha />} />
              <Route path="tecnicas" element={<AcademyBasicsTechniques />} />
              <Route path="equipo" element={<ViewCharacterSheet />} />
              <Route path="historia" element={<CharacterHistoria />} />
              <Route path="progresion" element={<CharacterProgresion />} />
            </Route>
            <Route path="/personajes/:slug/inventory" element={<InventoryRedirect />} />

            {/* DASHBOARD (legacy creation routes) */}
            <Route path="/dashboard/characters/new/rank" element={<DashNewRank />} />
            <Route path="/dashboard/characters/new/stat" element={<DashNewStat />} />
            <Route path="/dashboard/characters/new/shogunato" element={<DashNewShogunato />} />
            <Route path="/dashboard" element={<Navigate to={paths.admin} replace />} />
            <Route
              path="/admin/*"
              element={
                <AdminRouteGuard
                  permsLoaded={permsLoaded}
                  permsError={permsError}
                  hasAdminPerm={hasAdminPerm}
                />
              }
            />
            <Route path={paths.mapa} element={<WorldMap />} />

            {/* Redirects legacy (URLs en inglés) */}
            {LEGACY_REDIRECTS.map(({ from, to }) => (
              <Route
                key={from}
                path={from}
                element={<LegacyRedirect to={to} />}
              />
            ))}
          </Routes>
        </Suspense>
        {showNav && !isAdminRoute && <SiteFooter />}
      </>
    </ComingSoonGate>
  )
}

function App() {
  const [isInitiated, setIsInitiated] = useState(false)
  const [showNav, setShowNav] = useState(true)
  const [showCharNav, setShowCharNav] = useState(false)
  const [user, setUser] = useState(null)
  const [character, setCharacter] = useState(null)
  const [charInitiated, setCharInitiated] = useState(false)
  const [userPermissions, setUserPermissions] = useState([])
  const [permsLoaded, setPermsLoaded] = useState(false)
  const [permsError, setPermsError] = useState(false)
  const hasPermission = useCallback(
    (name) => Array.isArray(userPermissions) && userPermissions.includes(name),
    [userPermissions]
  )

  usePresenceHeartbeat({ user, character, enabled: isInitiated })

  const logout = () => {
    setUser(null)
    setCharacter(null)
    localStorage.removeItem('token')
  }

  const init = async () => {
    try {
      document.title = "Shinobis' War"
      const res = await HttpClient().get('/api/v1/user/init')
      // Solo establecer el usuario si tiene datos válidos (no es un usuario invitado)
      if (res.data.user && res.data.user.userId) {
        setUser(res.data.user)
      } else {
        setUser(null) // Usuario invitado
      }
      setIsInitiated(true)
    } catch (error) {
      console.error('Error during init:', error)
      setUser(null) // En caso de error, tratar como invitado
      setIsInitiated(true)
    }
  }

  const initChar = useCallback(async () => {
    setCharInitiated(false)
    try {
      const res = await HttpClient().get('/api/v1/characters/init')
      const data = res?.data
      setCharacter(data?.characterId ? data : null)
    } catch (error) {
      console.error('Error during initChar:', error)
      setCharacter(null)
    } finally {
      setCharInitiated(true)
    }
  }, [])

  const refreshActiveCharacter = useCallback(async () => {
    await initChar()
  }, [initChar])

  useEffect(() => {
    init()
  }, [])

  useEffect(() => {
    if (!isInitiated) return
    if (user?.userId) {
      initChar()
    } else {
      setCharacter(null)
      setCharInitiated(true)
    }
  }, [isInitiated, user?.userId, initChar])

  // Cargar permisos efectivos del usuario autenticado (RBAC existente)
  useEffect(() => {
    const fetchPerms = async () => {
      setPermsError(false)
      if (user && user.userId) {
        try {
          const res = await HttpClient().get('/api/v1/permissions/my-permissions')
          const perms = res?.data ?? res
          const list = Array.isArray(perms) ? perms : (perms?.permissions || [])
          const names = list.map((p) => (typeof p === 'string' ? p : p?.actionName)).filter(Boolean)
          setUserPermissions(names)
        } catch (_e) {
          setUserPermissions([])
          setPermsError(true)
        } finally {
          setPermsLoaded(true)
        }
      } else {
        setUserPermissions([])
        setPermsLoaded(true)
      }
    }
    fetchPerms()
  }, [user])

  return (
    <div>
      {isInitiated ? (
        <AppContext.Provider value={{ user, setUser, character, setCharacter, refreshActiveCharacter, logout, isInitiated, charInitiated, userPermissions, setUserPermissions, hasPermission, permsLoaded }}>
          <Toaster richColors position="top-right" />
          <Router>
            <AppRoutes
              user={user}
              showNav={showNav}
              showCharNav={showCharNav}
              setShowNav={setShowNav}
              setShowCharNav={setShowCharNav}
              permsLoaded={permsLoaded}
              permsError={permsError}
              hasAdminPerm={hasPermission('admin.dashboard')}
            />
          </Router>
        </AppContext.Provider>
      ) : (
        <div>Initializing...</div>
      )}
    </div>
  )
}

export default App
